"use client";

import { useState, useMemo, useCallback, useEffect } from "react";
import type {
  AirdropFarmingBotDto,
  AirdropFarmingExchangeId,
  AirdropFarmingPairData,
  AirdropFarmingSpotExchangeId,
  HubExchangeAccount,
} from "@cede/types";
import { AIRDROP_FARMING_BOT_TYPE, AIRDROP_FARMING_SPOT_SUPPORTED_EXCHANGES, SUPPORTED_EXCHANGES } from "@cede/types";
import type { AirdropFarmingStrategy } from "@cede/types";
import { AIRDROP_FARMING_STRATEGY } from "@cede/types";
import { getQuoteToken } from "@cede/utils";
import {
  DEFAULT_LEVERAGE,
  DEFAULT_NUM_LEVELS,
  DEFAULT_HIP3_PLATFORM,
  ESTIMATED_CYCLES_PER_HOUR,
  AIRDROP_FARMING_EXCHANGE_CONFIG,
  MIN_NOTIONAL_PER_LEVEL,
  SPOT_FEE_RATES,
  getMarketFeeRates,
  isSpotMode,
  type BotMode,
} from "@/constants/airdrop-farming";
import type { Hip3PlatformId, AirdropFarmingTabId } from "@/constants/airdrop-farming";
import { SUGGESTED_COST_PER_1K_USD } from "@/constants/airdrop-farming";
import { isHip3Platform } from "@/constants/airdrop-farming";
import type { PerpMarket } from "@/hooks/airdrop-farming/types";
import { MarketType, MarketCategory } from "@/hooks/airdrop-farming/types";
import { useAirdropFarmingBalance } from "@/hooks/airdrop-farming/useAirdropFarmingBalance";
import { useCreateBot } from "@/hooks/airdrop-farming/useCreateBot";
import { useExchangeSession } from "@/hooks/airdrop-farming/useExchangeSession";
import { useFeeTier } from "@/hooks/airdrop-farming/useFeeTier";
import { useMinCapital } from "@/hooks/airdrop-farming/useMinCapital";
import { useSuggestPairs } from "@/hooks/airdrop-farming/useSuggestPairs";
import { WALLET_ACTION_ROLE } from "@/hooks/core/useWalletActionGuard";
import { useHubUser } from "@/hooks/user/useHubUser";
import {
  computeEstimatedFeesToTarget,
  computeEstimatedFeeRange,
  computeEstimatedTimeHours,
  computeSizePerLevel,
  computeGridCycles,
  computeSafetyBudget,
  computeSuggestedMaxLoss,
} from "@/utils/airdrop-farming";
import Decimal from "decimal.js";
import { useChainId } from "wagmi";

/** HubExchangeAccount with exchangeId narrowed to AirdropFarmingExchangeId */
type AirdropFarmingAccount = Omit<HubExchangeAccount, "exchangeId"> & { exchangeId: AirdropFarmingExchangeId };

export type SupportedExchangeId = AirdropFarmingTabId;

/** Convert a backend pair score to the unified PerpMarket type used by the UI. */
function pairScoreToPerpMarket(score: AirdropFarmingPairData): PerpMarket {
  const quoteAsset = getQuoteToken(score.symbol);
  return {
    type: MarketType.PERP,
    symbol: score.symbol,
    pair: score.symbol,
    baseAsset: score.baseAsset,
    quoteAsset,
    markPrice: score.markPrice,
    volume24h: score.volume24hRaw,
    priceChange24h: score.priceChange24h,
    openInterest: score.openInterest,
    maxLeverage: score.maxLeverage,
    isGrowthMode: score.isGrowthMode,
    searchString: `${score.baseAsset}${score.symbol}`.toLowerCase(),
    categories: [MarketCategory.ALL],
  };
}

export const STEP_LABELS = ["Connect", "Market", "Configure", "Strategy", "Review"] as const;
export type StepIndex = 0 | 1 | 2 | 3 | 4;

export const GRID_BOT_STEPS = {
  CONNECT: 0,
  MARKET: 1,
  CONFIGURE: 2,
  STRATEGY: 3,
  REVIEW: 4,
} as const satisfies Record<string, StepIndex>;

export function useLaunchBotForm() {
  const [step, setStep] = useState<StepIndex>(0);
  const [exchangeId, setExchangeIdState] = useState<SupportedExchangeId>(SUPPORTED_EXCHANGES.HYPERLIQUID);
  const [selectedHip3Platform, setSelectedHip3Platform] = useState<Hip3PlatformId>(DEFAULT_HIP3_PLATFORM);
  const [customAgentName, setCustomAgentName] = useState("");
  const walletChainId = useChainId();
  const [botMode, setBotModeState] = useState<BotMode>(AIRDROP_FARMING_BOT_TYPE.PERP);
  const isSpot = isSpotMode(botMode);

  // Whether the selected exchange is Hyperliquid (HIP-3 sub-platforms)
  const isHip3 = exchangeId === SUPPORTED_EXCHANGES.HYPERLIQUID;

  // Hub user — needed for spot account lookup (CEX accounts linked via Settings)
  const { hubUser } = useHubUser();

  // Exchange session (connection + session status + accounts)
  const {
    connection,
    sessionStatus,
    accounts,
    selectedAccount,
    setSelectedAccount,
    handleSetupAction,
    isSettingUp,
    requiredWalletRole,
    requiredWalletAddress,
    exchangeAccountAddress,
    needsDeposit,
    preparationStep,
    setupLabel,
    refetchAgentData,
  } = useExchangeSession(exchangeId, {
    agentName: customAgentName || undefined,
  });
  const primaryWalletAddress = requiredWalletRole === WALLET_ACTION_ROLE.MAIN_EVM ? requiredWalletAddress : undefined;

  // Backend pair scores — used for all exchanges (HIP-3 + Paradex)
  const suggestPairsExchangeId = isHip3 ? selectedHip3Platform : exchangeId;
  const { pairScores, isLoadingScores } = useSuggestPairs({
    exchangeId: isSpot ? "" : suggestPairsExchangeId,
  });

  // Unified market list — all exchanges use backend scores
  const perpMarkets = useMemo(() => {
    return pairScores.map(pairScoreToPerpMarket);
  }, [pairScores]);
  const isLoadingMarkets = isLoadingScores;

  // Exchange config — drives UI display. For HIP-3, always use the Hyperliquid config.
  // For CEX/spot exchanges, this will be undefined — those code paths guard with isSpot.
  const exchangeConfig = AIRDROP_FARMING_EXCHANGE_CONFIG[exchangeId];

  // Spot-specific state
  const [spotPair, setSpotPair] = useState("");
  const spotAccounts: AirdropFarmingAccount[] = useMemo(
    () =>
      (hubUser?.exchangeAccounts ?? []).filter((a): a is AirdropFarmingAccount =>
        AIRDROP_FARMING_SPOT_SUPPORTED_EXCHANGES.has(a.exchangeId),
      ),
    [hubUser?.exchangeAccounts],
  );
  const [manualSpotAccount, setSelectedSpotAccount] = useState<AirdropFarmingAccount | null>(null);

  // Auto-select when there is a single spot account, otherwise use manual selection
  const selectedSpotAccount = useMemo(
    () =>
      isSpot && manualSpotAccount == null && spotAccounts.length === 1 ? (spotAccounts[0] ?? null) : manualSpotAccount,
    [isSpot, manualSpotAccount, spotAccounts],
  );

  // Switch bot mode — resets form state
  const setBotMode = useCallback(
    (mode: BotMode) => {
      if (mode === botMode) return;
      setBotModeState(mode);
      setStep(0);
      setSelectedMarketState(null);
      setSpotPair("");
      setSelectedSpotAccount(null);
      setCapital("");
      setLeverage(DEFAULT_LEVERAGE);
      setMaxLoss("");
      setTargetVolume("");
      setStrategyId(AIRDROP_FARMING_STRATEGY.DEPTH_PULSE);
    },
    [botMode],
  );

  // Chain check — wallet must be on the required chain before advancing
  // For spot/CEX exchanges, exchangeConfig is undefined — no chain requirement.
  const isOnRequiredChain = exchangeConfig?.requiredChainId == null || walletChainId === exchangeConfig.requiredChainId;
  const marginMode = exchangeConfig?.marginMode ?? "isolated";

  // Exchange-agnostic balance abstraction
  // In spot mode, use the spot account's exchange ID so the balance hook
  // correctly identifies it as non-Hyperliquid and uses backendBalance.
  const balanceExchangeId = isSpot
    ? ((selectedSpotAccount?.exchangeId as AirdropFarmingTabId) ?? exchangeId)
    : exchangeId;
  const [selectedMarket, setSelectedMarketState] = useState<PerpMarket | null>(null);
  const spotQuoteCoin = isSpot && spotPair.includes("/") ? spotPair.split("/")[1] : undefined;
  // For CEX perps (e.g. Bitget), pass the market's settlement token so the
  // balance hook fetches the correct futures wallet balance.
  const perpQuoteCoin = !isSpot ? selectedMarket?.quoteAsset : undefined;

  const balance = useAirdropFarmingBalance({
    exchangeId: balanceExchangeId,
    selectedHip3Platform,
    primaryWalletAddress,
    connection,
    // Only fetch balance after step 0 (connect) — verifyAndAdvance gates advancement
    accountId: step > 0 ? (isSpot ? (selectedSpotAccount?.id ?? null) : (selectedAccount?.id ?? null)) : null,
    quoteCoin: spotQuoteCoin ?? perpQuoteCoin,
  });

  // Form state
  const [capital, setCapital] = useState("");
  const [leverage, setLeverage] = useState(DEFAULT_LEVERAGE);
  const [maxLoss, setMaxLoss] = useState("");
  const [maxLossManuallyEdited, setMaxLossManuallyEdited] = useState(false);
  const [targetVolume, setTargetVolume] = useState("");
  const [riskProfile] = useState<"AGGRESSIVE" | "BALANCED" | "CONSERVATIVE">("BALANCED");
  const [strategyId, setStrategyId] = useState<AirdropFarmingStrategy>(AIRDROP_FARMING_STRATEGY.DEPTH_PULSE);

  // Effective leverage: always 1 for spot
  const effectiveLeverage = isSpot ? 1 : leverage;

  // Prefill / duplicate state
  const [prefillPair, setPrefillPair] = useState<string | null>(null);
  const [prefillAccountId, setPrefillAccountId] = useState<number | null>(null);
  const [isPrefilled, setIsPrefilled] = useState(false);

  // Whether user needs to swap other coins → settlement coin before launching.
  // Compares settlement coin balance (e.g. just USDC for TradeXYZ) against declared capital.
  const needsSwap =
    !isSpot && balance.supportsSwap && new Decimal(balance.settlementCoinBalance).lt(new Decimal(capital || "0"));

  // Reset market selection when switching exchanges
  const setExchangeId = useCallback((id: SupportedExchangeId) => {
    setExchangeIdState(id);
    setSelectedMarketState(null);
    if (id === SUPPORTED_EXCHANGES.HYPERLIQUID) {
      setSelectedHip3Platform(DEFAULT_HIP3_PLATFORM);
    }
  }, []);

  // Clamp leverage when market changes
  const setSelectedMarket = useCallback(
    (market: PerpMarket | null) => {
      setSelectedMarketState(market);
      if (market?.maxLeverage != null && leverage > market.maxLeverage) {
        setLeverage(market.maxLeverage);
      }
    },
    [leverage],
  );

  // Auto-match market from prefillPair when markets load (one-time)
  useEffect(() => {
    if (prefillPair && perpMarkets.length > 0) {
      const match = perpMarkets.find((m) => m.pair === prefillPair);
      if (match) {
        setSelectedMarket(match);
      }
      setPrefillPair(null);
    }
  }, [prefillPair, perpMarkets, setSelectedMarket]);

  // Auto-select the prefilled perp account when accounts load (one-time)
  useEffect(() => {
    if (prefillAccountId != null && accounts.length > 0) {
      const match = accounts.find((a) => a.id === prefillAccountId);
      if (match) {
        setSelectedAccount(match);
      }
      setPrefillAccountId(null);
    }
  }, [prefillAccountId, accounts, setSelectedAccount]);

  // Bot creation
  const { createBot, isCreating } = useCreateBot();

  // Derived computations
  const numLevels = DEFAULT_NUM_LEVELS;
  const hasCapital = capital !== "" && new Decimal(capital || "0").gt(0);
  const hasTargetVolume = targetVolume !== "" && new Decimal(targetVolume || "0").gt(0);
  const hasMaxLoss = maxLoss !== "" && new Decimal(maxLoss || "0").gt(0);
  const effectiveExchangeId = isHip3 ? selectedHip3Platform : exchangeId;
  const minCapitalPair = isSpot ? spotPair : selectedMarket?.pair;
  const minCapitalExchangeId = isSpot ? selectedSpotAccount?.exchangeId : effectiveExchangeId;
  const { data: minCapitalData, isFetching: isFetchingMinCapital } = useMinCapital({
    leverage: effectiveLeverage,
    maxLossUsdc: maxLoss || "0",
    numLevels,
    pair: minCapitalPair,
    exchangeId: minCapitalExchangeId,
    botType: botMode,
  });

  const capitalError = useMemo(() => {
    if (!capital || !hasMaxLoss) return null;
    const parsed = parseFloat(capital);
    if (isNaN(parsed) || parsed <= 0) return "Capital must be a positive number";

    if (isSpot) {
      if (minCapitalData) {
        const min = parseFloat(minCapitalData.minCapitalUsdc);
        if (parsed < min) {
          return `Minimum $${min.toFixed(2)} required for ${numLevels} grid levels.`;
        }
      } else {
        // Fallback while loading or pair not yet selected
        const spotMin = numLevels * MIN_NOTIONAL_PER_LEVEL * 2;
        if (parsed < spotMin) {
          return `Minimum $${spotMin.toFixed(2)} capital required for ${numLevels} grid levels.`;
        }
      }
      if (balance.availableUsd && balance.availableUsd !== "0") {
        const available = parseFloat(balance.availableUsd);
        if (!isNaN(available) && parsed > available) {
          return "Capital exceeds available balance.";
        }
      }
      return null;
    }

    if (!minCapitalData) return null;
    const min = parseFloat(minCapitalData.minCapitalUsdc);
    if (parsed < min) {
      const available = parseFloat(balance.availableUsd ?? "0");
      const maxLev = selectedMarket?.maxLeverage;
      if (!isNaN(available) && available < min && maxLev != null && leverage >= maxLev) {
        return `Insufficient balance. Minimum $${min.toFixed(2)} required at max leverage (${maxLev}x) but only $${available.toFixed(2)} available.`;
      }
      return `Minimum $${min.toFixed(2)} required at ${minCapitalData.leverage}x. Increase leverage to lower this.`;
    }
    if (balance.availableUsd) {
      const available = parseFloat(balance.availableUsd);
      if (!isNaN(available) && parsed > available) {
        return "Capital exceeds available balance.";
      }
    }
    return null;
  }, [
    capital,
    hasMaxLoss,
    minCapitalData,
    isSpot,
    numLevels,
    balance.availableUsd,
    leverage,
    selectedMarket?.maxLeverage,
  ]);

  const sizePerLevel = useMemo(() => {
    if (!hasCapital) return null;
    return computeSizePerLevel(capital, effectiveLeverage, numLevels);
  }, [capital, effectiveLeverage, numLevels, hasCapital]);

  const { data: feeTier } = useFeeTier({
    exchangeId: isHip3 ? selectedHip3Platform : exchangeId,
    exchangeAccountId: selectedAccount?.id ?? null,
  });

  const marketFeeRates = useMemo(() => {
    if (isSpot) {
      const spotExchangeId = selectedSpotAccount?.exchangeId as AirdropFarmingSpotExchangeId | undefined;
      if (spotExchangeId && SPOT_FEE_RATES[spotExchangeId]) return SPOT_FEE_RATES[spotExchangeId];

      return Object.values(SPOT_FEE_RATES)[0]!;
    }

    if (feeTier?.makerFee != null && feeTier?.takerFee != null) {
      return { maker: feeTier.makerFee, taker: feeTier.takerFee };
    }
    if (!exchangeConfig) return { maker: 0, taker: 0 };
    return getMarketFeeRates(isHip3, selectedMarket?.isGrowthMode === true, exchangeConfig, selectedHip3Platform);
  }, [
    isSpot,
    selectedSpotAccount?.exchangeId,
    feeTier,
    isHip3,
    selectedHip3Platform,
    selectedMarket?.isGrowthMode,
    exchangeConfig,
  ]);

  const estimation = useMemo(() => {
    if (!hasCapital || !hasTargetVolume) return null;
    const notional = new Decimal(capital).mul(effectiveLeverage);
    const feesToTarget = computeEstimatedFeesToTarget(targetVolume, marketFeeRates.maker.toString());
    const gridCycles = computeGridCycles(capital, effectiveLeverage, targetVolume);
    const safetyBudget = hasMaxLoss
      ? computeSafetyBudget(maxLoss, targetVolume, marketFeeRates.maker.toString())
      : null;
    const cyclesPerHour = ESTIMATED_CYCLES_PER_HOUR[riskProfile] ?? 12;
    const etaHours = computeEstimatedTimeHours(capital, effectiveLeverage, targetVolume, cyclesPerHour);
    const etaMs = etaHours * 3_600_000;

    // Per-market fee range (best case = all maker, worst case = all taker)
    const feeRange = hasTargetVolume ? computeEstimatedFeeRange(targetVolume, marketFeeRates) : null;

    return { notional, feesToTarget, gridCycles, safetyBudget, etaMs, feeRange };
  }, [
    capital,
    effectiveLeverage,
    targetVolume,
    maxLoss,
    riskProfile,
    marketFeeRates,
    hasCapital,
    hasTargetVolume,
    hasMaxLoss,
  ]);

  const canSubmit = isSpot
    ? selectedSpotAccount != null &&
      spotPair.includes("/") &&
      capital !== "" &&
      !capitalError &&
      !isFetchingMinCapital &&
      hasMaxLoss &&
      hasTargetVolume &&
      !isCreating
    : sessionStatus === "ready" &&
      selectedAccount != null &&
      selectedMarket != null &&
      capital !== "" &&
      !capitalError &&
      !isFetchingMinCapital &&
      hasMaxLoss &&
      hasTargetVolume &&
      !needsSwap &&
      !isCreating;

  // Step navigation
  const goNext = useCallback(() => {
    setStep((s) => (s < 4 ? ((s + 1) as StepIndex) : s));
  }, []);

  const goBack = useCallback(() => {
    setStep((s) => (s > 0 ? ((s - 1) as StepIndex) : s));
  }, []);

  const goToStep = useCallback((target: StepIndex) => {
    setStep((s) => (target < s ? target : s));
  }, []);

  // Unrestricted step jump — used by prefill flow to skip to Review
  const jumpToStep = useCallback((target: StepIndex) => {
    setStep(target);
  }, []);

  const prefill = useCallback(
    (bot: AirdropFarmingBotDto) => {
      const isBotSpot = bot.botType === AIRDROP_FARMING_BOT_TYPE.SPOT;

      if (isBotSpot) {
        // CEX/spot bot — set mode, exchange, pair, and match the original account
        setBotModeState(AIRDROP_FARMING_BOT_TYPE.SPOT);
        setExchangeIdState(bot.exchangeId as SupportedExchangeId);
        setSpotPair(bot.pair);

        // Match the spot account by exchangeAccountId so the form is ready to submit
        const matchedAccount =
          spotAccounts.find((a) => a.id === bot.exchangeAccountId) ??
          spotAccounts.find((a) => a.exchangeId === bot.exchangeId) ??
          null;
        setSelectedSpotAccount(matchedAccount);
      } else if (isHip3Platform(bot.exchangeId)) {
        setBotModeState(AIRDROP_FARMING_BOT_TYPE.PERP);
        setExchangeIdState(SUPPORTED_EXCHANGES.HYPERLIQUID);
        setSelectedHip3Platform(bot.exchangeId as Hip3PlatformId);
        setPrefillAccountId(bot.exchangeAccountId ?? null);
      } else {
        setBotModeState(AIRDROP_FARMING_BOT_TYPE.PERP);
        setExchangeIdState(bot.exchangeId as SupportedExchangeId);
        setPrefillAccountId(bot.exchangeAccountId ?? null);
      }

      setCapital(bot.capitalUsdc);
      setLeverage(bot.leverage);
      setMaxLoss(bot.maxLossUsdc);
      setMaxLossManuallyEdited(true);
      setTargetVolume(bot.targetVolumeUsdc);
      setStrategyId(bot.strategyId ?? AIRDROP_FARMING_STRATEGY.DEPTH_PULSE);
      setSelectedMarketState(null);
      setPrefillPair(isBotSpot ? null : bot.pair);
      setIsPrefilled(true);
      setStep(0);
    },
    [spotAccounts],
  );

  const reset = useCallback(() => {
    setStep(0);
    setExchangeIdState(SUPPORTED_EXCHANGES.HYPERLIQUID);
    setSelectedHip3Platform(DEFAULT_HIP3_PLATFORM);
    setBotModeState(AIRDROP_FARMING_BOT_TYPE.PERP);
    setSelectedMarketState(null);
    setCustomAgentName("");
    setSpotPair("");
    setSelectedSpotAccount(null);
    setCapital("");
    setLeverage(DEFAULT_LEVERAGE);
    setMaxLoss("");
    setMaxLossManuallyEdited(false);
    setTargetVolume("");
    setStrategyId(AIRDROP_FARMING_STRATEGY.DEPTH_PULSE);
    setPrefillPair(null);
    setPrefillAccountId(null);
    setIsPrefilled(false);
  }, []);

  const handleSubmit = useCallback(
    (onSuccess?: () => void) => {
      if (!canSubmit || !targetVolume) {
        return;
      }

      if (isSpot) {
        if (!selectedSpotAccount || !spotPair) return;
        createBot(
          {
            botType: AIRDROP_FARMING_BOT_TYPE.SPOT,
            exchangeAccountId: selectedSpotAccount.id,
            exchangeId: selectedSpotAccount.exchangeId,
            pair: spotPair,
            capitalUsdc: capital,
            targetVolumeUsdc: targetVolume,
            maxLossUsdc: maxLoss,
            riskProfile: "BALANCED",
            strategyId,
          },
          {
            onSuccess: () => {
              reset();
              onSuccess?.();
            },
          },
        );
      } else {
        if (!selectedAccount || !selectedMarket) return;
        createBot(
          {
            botType: AIRDROP_FARMING_BOT_TYPE.PERP,
            exchangeAccountId: selectedAccount.id,
            exchangeId: isHip3 ? selectedHip3Platform : exchangeId,
            marginMode,
            pair: selectedMarket.pair,
            walletAddress: requiredWalletAddress,
            capitalUsdc: capital,
            leverage,
            targetVolumeUsdc: targetVolume,
            maxLossUsdc: maxLoss,
            riskProfile: "BALANCED",
            strategyId,
          },
          {
            onSuccess: () => {
              reset();
              onSuccess?.();
            },
          },
        );
      }
    },
    [
      canSubmit,
      isSpot,
      selectedSpotAccount,
      spotPair,
      selectedAccount,
      selectedMarket,
      targetVolume,
      createBot,
      isHip3,
      selectedHip3Platform,
      exchangeId,
      marginMode,
      requiredWalletAddress,
      capital,
      leverage,
      maxLoss,
      strategyId,
      reset,
    ],
  );

  // Suggested max loss based on target volume ($1 per $1k traded)
  const suggestedMaxLoss = useMemo(() => {
    if (!hasTargetVolume) return null;
    return computeSuggestedMaxLoss(targetVolume, SUGGESTED_COST_PER_1K_USD);
  }, [targetVolume, hasTargetVolume]);

  const applySuggestedMaxLoss = useCallback(() => {
    if (suggestedMaxLoss != null) {
      setMaxLoss(String(suggestedMaxLoss));
      setMaxLossManuallyEdited(false);
    }
  }, [suggestedMaxLoss]);

  // Wrapper that marks max loss as manually edited
  const setMaxLossManual = useCallback((value: string) => {
    setMaxLoss(value);
    setMaxLossManuallyEdited(true);
  }, []);

  // Sets target volume and auto-fills max loss if not manually edited
  const setTargetVolumeWithAutoMaxLoss = useCallback(
    (value: string) => {
      setTargetVolume(value);
      if (!maxLossManuallyEdited && value) {
        const suggested = computeSuggestedMaxLoss(value, SUGGESTED_COST_PER_1K_USD);
        if (suggested > 0) setMaxLoss(String(suggested));
      }
    },
    [maxLossManuallyEdited],
  );

  return {
    // Step navigation
    step,
    goNext,
    goBack,
    goToStep,
    jumpToStep,
    reset,
    prefill,
    isPrefilled,

    // Bot mode
    botMode,
    setBotMode,
    isSpot,

    // Exchange selection
    exchangeId,
    setExchangeId,
    isHip3,
    selectedHip3Platform,
    setSelectedHip3Platform,
    customAgentName,
    setCustomAgentName,

    // Connection
    connection,
    sessionStatus,
    requiredWalletRole,
    requiredWalletAddress,
    exchangeAccountAddress,
    isOnRequiredChain,

    // Account (perp)
    accounts,
    selectedAccount,
    setSelectedAccount,

    // Account (spot)
    spotAccounts,
    selectedSpotAccount,
    setSelectedSpotAccount,
    spotPair,
    setSpotPair,

    // Markets
    perpMarkets,
    isLoadingMarkets,
    selectedMarket,
    setSelectedMarket,

    // Balance
    primaryWalletAddress,
    spotQuoteCoin,
    availableUsd: balance.availableUsd,
    isFetchingBalance: balance.isFetchingBalance,
    hasFetchedBalance: balance.hasFetchedBalance,
    settlementCoin: balance.settlementCoin,
    settlementCoinBalance: balance.settlementCoinBalance,
    needsSwap,
    spotUsdc: balance.spotUsdc,
    spotUsdcForSwap: balance.spotUsdcForSwap,
    getSpotBalance: balance.getSpotBalance,

    // Transfer & swap
    handleTransferToPerps: balance.transferToPerps,
    isTransferring: balance.isTransferring,
    refetchBalances: balance.refetchBalances,
    accountId: connection.agentData.accountId ?? null,

    // Form fields
    capital,
    setCapital,
    leverage,
    effectiveLeverage,
    setLeverage,
    maxLoss,
    setMaxLoss: setMaxLossManual,
    targetVolume,
    setTargetVolume: setTargetVolumeWithAutoMaxLoss,
    riskProfile,
    strategyId,
    setStrategyId,
    applySuggestedMaxLoss,

    // Derived
    marginMode,
    capitalError,
    hasCapital,
    hasTargetVolume,
    hasMaxLoss,
    isFetchingMinCapital,
    sizePerLevel,
    estimation,
    suggestedMaxLoss,
    marketFeeRates,
    canSubmit,
    isCreating,

    // Actions
    handleSubmit,
    handleSetupAction,
    isSettingUp,
    needsDeposit,
    preparationStep,
    setupLabel,
    refetchAgentData,
  };
}

export type LaunchBotFormState = ReturnType<typeof useLaunchBotForm>;
